// TODO: 404 for /info page if there's no site description

import React, { Component } from "react";
import {
  Route,
  Switch,
  Redirect,
  withRouter,
  RouteComponentProps
} from "react-router-dom";
import { Helmet } from "react-helmet";
import Amplify, { I18n } from "@aws-amplify/core";
import styled, { ThemeProvider } from "styled-components";

import { fetchSiteConfig, fetchSiteLanguages } from "../../api/SiteApi";
import BibleInfo from "../BibleInfo/BibleInfo";
import SiteInfo from "../SiteInfo/SiteInfo";
import SiteWrapper from "./SiteWrapper";
import config from "../../config";
import redirectTo404 from "../../utils/redirectTo404";
import { initAnalytics } from "utils/analytics/initialize";
import { isCordovaApp } from "../../utils/cordovaUtils";
import { setSiteDictionary } from "../../utils/i18n";
import AsyncComponent from "../AsyncComponent";
import { Bible } from "../../api/typedefs/Bible";
import { Site } from "../../api/typedefs/Site";
import SearchResults from "../SearchResults/SearchResults";
import BibleViewer from "components/Bible/BiblePage";
import { defaultSite } from "./Contexts/SiteConfig";
import { themeFromSiteConfig } from "components/Site/Contexts/SiteTheme";
import CookieMessage from "components/UI/CookieMessage";
import PWAInstallPrompt from "components/UI/PWAInstallPrompt";

Amplify.configure(config.cognitoConfig);

const {
  REACT_APP_NO_REDIRECT,
  REACT_APP_FUMS_SOURCE,
  REACT_APP_FUMS_REPORT_URL,
  REACT_APP_GA_ID
} = process.env;
const fumsScriptSrc =
  REACT_APP_FUMS_SOURCE || "https://pkg.api.bible/fumsV3.min.js";
const fumsReportUrl = REACT_APP_FUMS_REPORT_URL || ""; //fumsV3 script will assume https://fums.api.bible by default if it is not defined

declare global {
  interface Window {
    fums: any;
    fumsData: any[];
  }
}

const AsyncWidgetBuilder = AsyncComponent(() =>
  import("../WidgetBuilder/WidgetBuilder")
);

const AsyncLegacyChapterRedirector = AsyncComponent(() =>
  import("../LegacyRedirector/LegacyChapterRedirector")
);

const AsyncPrivacyPage = AsyncComponent(() =>
  import("../PolicyPages").then(({ PrivacyPage }) => ({ default: PrivacyPage }))
);

const AsyncTermsPage = AsyncComponent(() =>
  import("../PolicyPages").then(({ TermsPage }) => ({ default: TermsPage }))
);

const CustomCookieMessage = styled(CookieMessage)`
  z-index: 1;
`;

interface SiteContainerProps extends RouteComponentProps {
  hostname: string;
}

interface SiteContainerState {
  fetchStatus: FetchStatus;
  siteNotFound: boolean;
  site: Site;
}

interface MatchParams {
  params: {
    bibleId: string;
  };
}

enum FetchStatus {
  Idle,
  Loading,
  Success,
  Error
}

const defaultState: SiteContainerState = {
  fetchStatus: FetchStatus.Idle,
  siteNotFound: true,
  site: defaultSite
};

class SiteContainer extends Component<SiteContainerProps, SiteContainerState> {
  state = defaultState;

  async componentDidMount() {
    let { hostname } = this.props;

    // fetch site config
    let site: Site;
    try {
      this.setState({ fetchStatus: FetchStatus.Loading });
      const siteConfig = await fetchSiteConfig(hostname);
      if (siteConfig === undefined) this.setState({ siteNotFound: true });
      site = siteConfig as Site;
    } catch (err) {
      console.error(err);
      return this.setState({ fetchStatus: FetchStatus.Error });
    }

    // init google analytics for site
    initAnalytics(site, hostname);

    // init fums analytics for site (this should match code in API.Bible documentation)
    (window as any).fumsData = (window as any).fumsData || [];
    (window as any).fums =
      (window as any).fums ||
      function() {
        (window as any).fumsData.push(arguments);
      };
    if (fumsReportUrl) window.fums("config", { baseUrl: fumsReportUrl });

    this.setState({ site, fetchStatus: FetchStatus.Success });

    const { primaryLanguage, secondaryLanguage } = site;
    if (primaryLanguage || secondaryLanguage) {
      const languages = await fetchSiteLanguages({
        primaryLanguage,
        secondaryLanguage
      });
      if (languages) setSiteDictionary(languages, primaryLanguage);
      this.forceUpdate();
    }
  }

  render() {
    const { site, fetchStatus } = this.state;

    if (fetchStatus === FetchStatus.Error && !REACT_APP_NO_REDIRECT) {
      redirectTo404();
    }

    if (fetchStatus === FetchStatus.Idle) return null;
    // TODO: provide better loading experience
    if (fetchStatus === FetchStatus.Loading) return null;

    // TODO: FIX "testing" should not be a fallback
    const defaultBible =
      site.bibles && site.bibles.length ? (site.bibles[0] as Bible) : undefined;
    const defaultBibleId = (defaultBible && defaultBible.id) || "testing";

    return (
      <SiteWrapper site={site}>
        <Helmet>
          <title>{site.title}</title>
          <meta name="theme-color" content={site.primaryColor} />
          <meta name="og:type" content="website" />
          <meta name="og:site_name" content={site.title} />
          <meta name="application-name" content={site.appStoreName} />
          <meta name="msapplication-TileColor" content={site.primaryColor} />
          <link
            href="https://fonts.googleapis.com/css?family=Noto+Sans:400,400i|Noto+Serif:400,400i&display=swap"
            rel="stylesheet"
          />
          <link
            rel="mask-icon"
            href="/safari-pinned-tab.svg"
            color={site.primaryColor}
          />

          <script
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${REACT_APP_GA_ID}`}
          />
          <script>
            {`window.dataLayer = window.dataLayer || [];
              function gtag() {
                dataLayer.push(arguments);
              }
              gtag("js", new Date());
              gtag("config", "${REACT_APP_GA_ID}")
            `}
          </script>

          {fumsScriptSrc && (
            <script type="text/javascript" src={fumsScriptSrc} />
          )}
        </Helmet>

        <Switch>
          <Route path="/bible/:bibleId/info" component={BibleInfo} />
          <Route path="/bible/:bibleId/search" component={SearchResults} />
          <Route
            path="/bible/:bibleId/:chapterId"
            render={props => <BibleViewer site={site} />}
          />
          <Route path="/info" render={() => <SiteInfo site={site} />} />
          <Route path="/widget" component={AsyncWidgetBuilder} />
          <Route
            path="/privacy"
            render={() => <AsyncPrivacyPage site={site} />}
          />
          <Route path="/terms" render={() => <AsyncTermsPage site={site} />} />

          <Redirect
            exact
            from="/"
            to={`/bible/${defaultBibleId}/${config.defaultChapter}`}
          />

          <Route
            path="/search/:queryString"
            render={({ history, match }) => {
              const urlQueryParams = history.location.search;
              const { queryString } = match.params;

              return (
                <Redirect
                  to={`/bible/${defaultBibleId}/search?q=${queryString}${
                    urlQueryParams ? "&" + urlQueryParams : ""
                  }`}
                />
              );
            }}
          />
          <Route
            path="/:legacyBible(\w\w\w-\w+)/:legacyBook(\w+)/:chapterNumber"
            component={AsyncLegacyChapterRedirector}
          />
        </Switch>

        <Route
          render={({ location: { pathname } }) => {
            // don't display on cordova apps
            if (isCordovaApp) return null;
            // don't display on T&C and privacy pages
            if (pathname === "/terms" || pathname === "/privacy") return null;

            return (
              <>
                <PWAInstallPrompt />
                <ThemeProvider theme={themeFromSiteConfig(site)}>
                  <CustomCookieMessage>
                    {I18n.get(
                      "This website uses cookies and other tracking technologies to ensure you get the best experience on our website. For more information see our"
                    )}{" "}
                    <a href="/privacy" target="_blank">
                      {I18n.get("privacy policy")}
                    </a>
                    .
                  </CustomCookieMessage>
                </ThemeProvider>
              </>
            );
          }}
        />
      </SiteWrapper>
    );
  }
}

export default withRouter(SiteContainer);
